import ConductionAPI from '@/javascript/conduction-api';
import { Utils } from '@/javascript/utils';
import { Button, Group, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck, IconLock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface PasswordResetModalProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = (props) => {
	const { showModal, setShowModal } = props;
  	const [searchParams] = useSearchParams();
	const [ userId, setUserId ] = useState<string | null>(null);
	const [ token, setToken ] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		const newUserId = searchParams.get('userId');
		const newToken = searchParams.get('token');	
		setUserId(newUserId);
		setToken(newToken);

		if (!newUserId || !newToken) {
			setShowModal(false);
		}
	}, [searchParams]);

	const form = useForm({
		initialValues: {
			newPassword: '',
			confirmPassword: '',
		},
		validate: {
			newPassword: (value: string) => {
				if (value.length < 6 || value.length > 30) return 'Password must be between 6 and 30 characters';
				if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
				if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
				if (!/[0-9]/.test(value)) return 'Password must contain a number';
				if (!/[^a-z\d]/i.test(value)) return 'Password must contain a special character';
				return null;
			},
			confirmPassword: (value: string, values: { newPassword: string }) =>
				value !== values.newPassword ? 'Passwords do not match' : null,
		},
	});

	const handleSubmit = async () => {
		if (form.validate().hasErrors) {
			return;	
		}

		if (!userId || !token) {
			console.error("User ID or token is missing.");
			setShowModal(false);
			return;
		}

		const notificaitonId = Utils.displayNotification("Attempting password reset...", null, "blue", "loading", false);

		setIsSubmitting(true);
		const success = await ConductionAPI.resetPassword(
			userId,
			token,
			form.values.newPassword
		);
		setIsSubmitting(false);

		if (success) {
			Utils.updateNotification(notificaitonId, "Password Reset Successful", "Your password has been reset successfully.", "green", <IconCheck size="18"/>, true);
			setShowModal(false);
		} else {
			Utils.updateNotification(notificaitonId, "Password Reset Failed", "Failed to reset password. Please try again.", "red", <IconAlertCircle size="18"/>, true, false);
		}
	};

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="Password Reset"
			centered
			size="md"
			styles={{
				title: {
					fontSize: 26,
					fontVariant: "all-small-caps"
				}
			}}
		>
			<Stack>
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<PasswordInput
							label={
								<Group>
									<IconLock size={20} />
									<Text>New Password</Text>
								</Group>
							}
							{...form.getInputProps('newPassword')}
							required
							withAsterisk={false}
							disabled={isSubmitting}
						/>

						<PasswordInput
							label={
								<Group>
									<IconLock size={20} />
									<Text>Confirm Password</Text>
								</Group>
							}
							{...form.getInputProps('confirmPassword')}
							required
							withAsterisk={false}
							disabled={isSubmitting}
						/>

						<Button
							type="submit"
							radius="xl"
							size='sm'
							loading={isSubmitting}
						>
							Set Password
						</Button>
					</Stack>
				</form>
			</Stack>
		</Modal>
	);
};

export default PasswordResetModal;
